/**
 * Ordens de serviço, contratos e recibos do período.
 *
 * Cada entidade conserva seu vocabulário. Em especial, recibo usa o estado
 * DERIVADO de `cancelled_at`; a tabela não tem coluna `status` (#57).
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllPages, type PagedRows } from '@/lib/reportPaging';
import { todayIsoDate } from '@/lib/financeStatus';
import {
  documentSortServerColumn,
  type ContractDocument,
  type DocumentsReportSort,
  type ReceiptDocument,
  type ServiceOrderDocument,
} from '@/lib/documentsReport';
import type { IsoDateRange } from '@/lib/paymentsReport';
import {
  contractMatchesReportFilters,
  parseDocumentStatusToken,
  receiptMatchesReportFilters,
  serviceOrderMatchesReportFilters,
  type ReportFilterState,
} from '@/lib/reportFilters';
import {
  getCachedContracts,
  getCachedReceipts,
  getCachedServiceOrders,
} from '@/lib/offlineDataCache';

export interface ReportDocumentsResult {
  serviceOrders: ServiceOrderDocument[];
  contracts: ContractDocument[];
  receipts: ReceiptDocument[];
  truncated: boolean;
}

export interface ReportDocumentsOptions {
  range: { from: Date; to: Date };
  isoRange: IsoDateRange;
  filters: ReportFilterState;
  sort: DocumentsReportSort;
  enabled: boolean;
}

const EMPTY_PAGE: PagedRows<any> = { rows: [], truncated: false };

function localIsoDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : todayIsoDate(parsed);
}

function inTimestampRange(row: any, range: { from: Date; to: Date }): boolean {
  const date = new Date(row?.created_at);
  return !Number.isNaN(date.getTime()) && date >= range.from && date <= range.to;
}

function inDateRange(raw: unknown, range: IsoDateRange): boolean {
  const date = String(raw ?? '');
  return Boolean(date) && date >= range.from && date <= range.to;
}

function toServiceOrder(row: any): ServiceOrderDocument {
  return {
    id: String(row?.id ?? ''),
    status: row?.status ?? null,
    date: localIsoDate(row?.created_at),
    total: row?.total ?? 0,
    quoteId: row?.quote_id ?? null,
    clientId: row?.client_id ?? null,
    clientName: row?.client_name ?? null,
    title: row?.title ?? null,
    items: (row?.service_order_items ?? row?._offline_items ?? row?.items ?? []).map(
      (item: any) => ({
        name: item?.name ?? null,
        product_id: item?.product_id ?? null,
        quantity: item?.quantity,
        unit_price: item?.unit_price,
        unit_cost: item?.unit_cost,
        discount: item?.discount,
        discount_type: item?.discount_type,
      }),
    ),
  };
}

function toContract(row: any): ContractDocument {
  return {
    id: String(row?.id ?? ''),
    status: row?.status ?? null,
    date: localIsoDate(row?.created_at),
    total: row?.total ?? 0,
    clientId: row?.client_id ?? null,
    clientName: row?.client_name ?? null,
    title: row?.title ?? null,
    signedAt: row?.client_signed_at ?? null,
  };
}

function toReceipt(row: any): ReceiptDocument {
  return {
    id: String(row?.id ?? ''),
    date: row?.issue_date ?? null,
    amount: row?.amount ?? 0,
    cancelledAt: row?.cancelled_at ?? null,
    clientId: row?.client_id ?? null,
    payerName: row?.payer_name ?? null,
    paymentMethod: row?.payment_method ?? null,
  };
}

function cachedResult(
  companyId: string,
  options: Pick<ReportDocumentsOptions, 'range' | 'isoRange' | 'filters'>,
  serviceOrders: readonly any[],
  contracts: readonly any[],
  receipts: readonly any[],
): ReportDocumentsResult {
  const company = (row: any) => !row?.company_id || row.company_id === companyId;
  return {
    serviceOrders: serviceOrders
      .filter(company)
      .filter((row) => inTimestampRange(row, options.range))
      .filter((row) => serviceOrderMatchesReportFilters(row, options.filters))
      .map(toServiceOrder),
    contracts: contracts
      .filter(company)
      .filter((row) => inTimestampRange(row, options.range))
      .filter((row) => contractMatchesReportFilters(row, options.filters))
      .map(toContract),
    receipts: receipts
      .filter(company)
      .filter((row) => inDateRange(row?.issue_date, options.isoRange))
      .filter((row) => receiptMatchesReportFilters(row, options.filters))
      .map(toReceipt),
    truncated: false,
  };
}

/**
 * A assinatura por objeto é intencional (#71): novos filtros quebram call-sites
 * antigos no `tsc -b`, em vez de serem silenciosamente ignorados.
 */
export function useReportDocuments(options: ReportDocumentsOptions) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const { range, isoRange, filters, sort, enabled } = options;

  const fromIso = range.from.toISOString();
  const toIso = range.to.toISOString();
  const clientId = filters.clientId;
  const statusToken = filters.statusByTab.documents;
  const contextualStatus = parseDocumentStatusToken(statusToken);

  const query = useQuery<ReportDocumentsResult>({
    queryKey: [
      'report-documents',
      companyId,
      fromIso,
      toIso,
      isoRange.from,
      isoRange.to,
      clientId,
      statusToken,
      sort.field,
      sort.direction,
    ],
    enabled: Boolean(user?.id && companyId) && enabled,
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => {
      if (!user?.id || !companyId) return undefined;
      return cachedResult(
        companyId,
        { range, isoRange, filters },
        getCachedServiceOrders(user.id) ?? [],
        getCachedContracts(user.id) ?? [],
        getCachedReceipts(user.id) ?? [],
      );
    },
    queryFn: async () => {
      if (!companyId) {
        return { serviceOrders: [], contracts: [], receipts: [], truncated: false };
      }
      const ascending = sort.direction === 'asc';

      const loadOrders = contextualStatus && contextualStatus.type !== 'service_order'
        ? Promise.resolve(EMPTY_PAGE)
        : fetchAllPages<any>((start, end) => {
            let query: any = supabase
              .from('service_orders')
              .select(
                'id, status, created_at, total, quote_id, client_id, client_name, title,'
                + ' service_order_items(name, product_id, quantity, unit_price, unit_cost,'
                + ' discount, discount_type)',
              )
              .eq('company_id', companyId)
              .gte('created_at', fromIso)
              .lte('created_at', toIso);
            // BLOCO OS: filtros dentro do callback paginado.
            if (clientId) query = query.eq('client_id', clientId);
            if (contextualStatus) query = query.eq('status', contextualStatus.status);
            return query
              .order(documentSortServerColumn(sort.field, 'service_order'), { ascending })
              .order('id', { ascending: true })
              .range(start, end);
          });

      const loadContracts = contextualStatus && contextualStatus.type !== 'contract'
        ? Promise.resolve(EMPTY_PAGE)
        : fetchAllPages<any>((start, end) => {
            let query: any = supabase
              .from('contracts')
              .select(
                'id, status, created_at, total, client_id, client_name, title, client_signed_at',
              )
              .eq('company_id', companyId)
              .gte('created_at', fromIso)
              .lte('created_at', toIso);
            // BLOCO CONTRATOS: filtros dentro do callback paginado.
            if (clientId) query = query.eq('client_id', clientId);
            if (contextualStatus) query = query.eq('status', contextualStatus.status);
            return query
              .order(documentSortServerColumn(sort.field, 'contract'), { ascending })
              .order('id', { ascending: true })
              .range(start, end);
          });

      const loadReceipts = contextualStatus && contextualStatus.type !== 'receipt'
        ? Promise.resolve(EMPTY_PAGE)
        : fetchAllPages<any>((start, end) => {
            let query: any = supabase
              .from('receipts')
              // `status` NÃO existe: estado derivado exclusivamente de cancelled_at.
              .select(
                'id, issue_date, amount, cancelled_at, client_id, payer_name, payment_method',
              )
              .eq('company_id', companyId)
              .gte('issue_date', isoRange.from)
              .lte('issue_date', isoRange.to);
            // BLOCO RECIBOS: filtros dentro do callback paginado.
            if (clientId) query = query.eq('client_id', clientId);
            if (contextualStatus?.status === 'issued') {
              query = query.is('cancelled_at', null);
            } else if (contextualStatus?.status === 'cancelled') {
              query = query.not('cancelled_at', 'is', null);
            }
            return query
              .order(documentSortServerColumn(sort.field, 'receipt'), {
                ascending,
                // `cancelled_at` representa status: não nulo = cancelado,
                // nulo = emitido. Ordem lexical: cancelado antes de emitido.
                nullsFirst: sort.direction === 'desc',
              })
              .order('id', { ascending: true })
              .range(start, end);
          });

      const [orders, contracts, receipts] = await Promise.all([
        loadOrders,
        loadContracts,
        loadReceipts,
      ]);

      return {
        serviceOrders: orders.rows
          .filter((row) => serviceOrderMatchesReportFilters(row, filters))
          .map(toServiceOrder),
        contracts: contracts.rows
          .filter((row) => contractMatchesReportFilters(row, filters))
          .map(toContract),
        receipts: receipts.rows
          .filter((row) => receiptMatchesReportFilters(row, filters))
          .map(toReceipt),
        truncated: orders.truncated || contracts.truncated || receipts.truncated,
      };
    },
  });

  return {
    serviceOrders: query.data?.serviceOrders ?? [],
    contracts: query.data?.contracts ?? [],
    receipts: query.data?.receipts ?? [],
    truncated: query.data?.truncated ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
